import React from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LegalNotice from './LegalNotice';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const DashboardLayout = () => {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const isAdvisor = profile?.role === 'advisor_admin' || profile?.role === 'advisor_user';
  const isLawFirm = profile?.role === 'attorney' || profile?.role === 'legal_staff';
  const isClient = profile?.role === 'client';
  const isPlatformAdmin = profile?.role === 'platform_admin';

  const navLinks = [
    { name: 'Dashboard', path: '/dashboard', icon: FiIcons.FiHome, show: isAdvisor || isLawFirm || isClient || isPlatformAdmin },
    { name: 'Clients', path: '/clients', icon: FiIcons.FiUsers, show: isAdvisor },
    { name: 'Plans', path: '/matters', icon: FiIcons.FiFolder, show: isAdvisor },
    { name: 'Add Client', path: '/clients/create', icon: FiIcons.FiUserPlus, show: isAdvisor },
    { name: 'Create Plan', path: '/matters/create', icon: FiIcons.FiPlusSquare, show: isAdvisor },
    { name: 'Billing', path: '/billing', icon: FiIcons.FiCreditCard, show: isAdvisor },
    { name: 'Assigned Plans', path: '/law-firm/matters', icon: FiIcons.FiFolder, show: isLawFirm },
    { name: 'Tasks', path: '/law-firm/tasks', icon: FiIcons.FiCheckSquare, show: isLawFirm },
    { name: 'Learning Content', path: '/admin/learning-content', icon: FiIcons.FiBookOpen, show: isPlatformAdmin },
    { name: 'Learning Tracks', path: '/admin/learning-tracks', icon: FiIcons.FiList, show: isPlatformAdmin },
    { name: 'Learning Center', path: '/learning', icon: FiIcons.FiBookOpen, show: !!profile },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
                <SafeIcon icon={FiIcons.FiBriefcase} className="h-6 w-6 text-slate-800 mr-2" />
                <span className="font-bold text-xl tracking-tight text-slate-900">EstatePlatform</span>
              </div>
              
              {/* Desktop Nav Links */}
              <div className="hidden md:ml-8 md:flex md:space-x-4">
                {navLinks.filter(l => l.show).map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === link.path
                        ? 'text-slate-900 bg-slate-100'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <SafeIcon icon={link.icon} className="mr-2 h-4 w-4" />
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <div className="text-sm font-semibold text-slate-900 leading-none">
                  {profile?.first_name} {profile?.last_name}
                </div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mt-1">
                  {profile?.role?.replace('_', ' ')}
                </div>
              </div>
              <button
                onClick={handleSignOut}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                title="Sign Out"
              >
                <SafeIcon icon={FiIcons.FiLogOut} className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Footer */}
      <LegalNotice />
    </div>
  );
};

export default DashboardLayout;
